import { useState, useRef } from "react";
import {
  Play, Pause, Volume2, VolumeX, Users, Music,
  Share2, Clock, Check, ChevronDown, ListMusic
} from "lucide-react";
import FFTVisualizer from "../player/FFTVisualizer";

const StationPlayerCard = ({
  config, np, isPlaying, volume, isMuted, listeners,
  mounts, selectedMount, showMountDropdown, setShowMountDropdown,
  analyzerRef, showVis, stationName, tagline, logo, isLive, pc,
  onTogglePlay, onVolumeChange, onMute, onSwitchMount, onShare,
  copied, showHistory, setShowHistory, history
}) => {
  const [showVolLabel, setShowVolLabel] = useState(false);
  return (
    <div className="relative w-full max-w-lg z-10">
      {showMountDropdown && (
        <div className="fixed inset-0 z-20" onClick={() => setShowMountDropdown(false)} />
      )}

      <div
        className="relative backdrop-blur-xl rounded-2xl overflow-hidden shadow-2xl"
        style={{
          border: `1px solid ${pc}33`,
          backgroundColor: (config.player_card_color || pc) + "cc",
        }}
        data-testid="station-player-card"
      >
        {/* Header */}
        <div className="px-5 pt-4 pb-2 text-center">
          {logo && (
            <img src={logo} alt={stationName} className="w-20 h-20 rounded-xl mx-auto mb-2 object-contain" />
          )}
          <h1 className="text-lg font-bold text-white drop-shadow-lg" data-testid="station-name">
            {stationName}
          </h1>
          {tagline && <p className="text-white/60 text-[11px] mt-0.5">{tagline}</p>}
        </div>

        {/* Now Playing */}
        <div className="px-5 pb-2">
          <div className="flex items-center gap-3">
            {np.song?.art ? (
              <img src={np.song.art} alt="" className="w-[60px] h-[60px] rounded-xl object-cover shadow-lg flex-shrink-0 border border-white/10" />
            ) : (
              <div className="w-[60px] h-[60px] rounded-xl bg-black/20 flex items-center justify-center flex-shrink-0 border border-white/10">
                <Music className="w-6 h-6 text-white/20" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-base truncate drop-shadow">
                {np.song?.title || "---"}
              </p>
              <p className="text-white/60 text-sm truncate">{np.song?.artist || "---"}</p>
              <div className="mt-2 flex items-center gap-2">
                <span
                  className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded animate-pulse"
                  style={{ color: "#fff", backgroundColor: isLive ? "#ef4444" : "#16a34a" }}
                  data-testid="on-air-badge"
                >
                  {isLive ? "LIVE" : "ON AIR"}
                </span>
                <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{ width: isPlaying ? "100%" : "0%", backgroundColor: "#fff", opacity: 0.4 }}
                  />
                </div>
                <span className="text-[10px] text-white/30 flex items-center gap-1">
                  <Users className="w-3 h-3" /> {listeners}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Next Up */}
        {np.playing_next?.song && (
          <div className="px-5 pb-2">
            <div className="flex items-center gap-2.5 bg-black/15 rounded-lg p-2">
              {np.playing_next.song.art ? (
                <img src={np.playing_next.song.art} alt="" className="w-9 h-9 rounded-lg object-cover flex-shrink-0" />
              ) : (
                <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Music className="w-4 h-4 text-white/20" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-white/40 uppercase tracking-wider font-semibold">Next Up</p>
                <p className="text-white text-xs font-medium truncate">{np.playing_next.song.title || "Unknown"}</p>
                <p className="text-white/40 text-[10px] truncate">{np.playing_next.song.artist || ""}</p>
              </div>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="px-5 pb-3">
          <div className="flex items-center gap-3">
            <button
              onClick={onTogglePlay}
              className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-105 flex-shrink-0"
              style={{ backgroundColor: isPlaying ? "rgba(255,255,255,0.15)" : "#ffffff" }}
              data-testid="station-play-btn"
            >
              {isPlaying ? (
                <Pause className="w-5 h-5 text-white" />
              ) : (
                <Play className="w-5 h-5 text-black ml-0.5" />
              )}
            </button>

            {mounts.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => setShowMountDropdown(!showMountDropdown)}
                  className="flex items-center gap-1 bg-white/10 hover:bg-white/15 text-white/80 px-2.5 py-1.5 rounded-lg text-xs font-medium transition"
                >
                  <span>
                    {selectedMount
                      ? `${(selectedMount.autodj_format || selectedMount.format || "mp3").toUpperCase()} ${selectedMount.autodj_bitrate || selectedMount.bitrate || ""}kbps`
                      : "Stream"}
                  </span>
                  <ChevronDown className="w-3 h-3" />
                </button>
                {showMountDropdown && (
                  <div className="absolute top-full left-0 mt-1 bg-gray-900 border border-white/10 rounded-xl shadow-2xl z-30 min-w-[150px] py-1">
                    {mounts.map((m, i) => (
                      <button
                        key={i}
                        onClick={() => onSwitchMount(m)}
                        className={`w-full text-left px-3 py-2 text-sm transition flex items-center justify-between ${
                          selectedMount?.id === m.id ? "bg-white/10 text-white" : "text-white/60 hover:bg-white/5 hover:text-white"
                        }`}
                      >
                        <span>{(m.autodj_format || m.format || "mp3").toUpperCase()}</span>
                        <span className="text-white/30 text-xs">{m.autodj_bitrate || m.bitrate || "?"}kbps</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center gap-2 flex-1 ml-auto relative">
              <button onClick={onMute} className="text-white/40 hover:text-white transition">
                {isMuted || volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
              <div className="flex-1 relative">
                {showVolLabel && (
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black/90 text-white text-xs font-bold px-2 py-1 rounded-md whitespace-nowrap pointer-events-none z-50"
                    style={{ left: `${(isMuted ? 0 : volume) * 100}%` }}
                    data-testid="station-volume-tooltip">
                    {Math.round((isMuted ? 0 : volume) * 100)}%
                  </div>
                )}
                <input
                  type="range" min="0" max="1" step="0.01"
                  value={isMuted ? 0 : volume}
                  onChange={onVolumeChange}
                  onMouseDown={() => setShowVolLabel(true)}
                  onMouseUp={() => setShowVolLabel(false)}
                  onTouchStart={() => setShowVolLabel(true)}
                  onTouchEnd={() => setShowVolLabel(false)}
                  className="w-full h-1.5 rounded-full appearance-none cursor-pointer
                    [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
                    [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md
                    [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-none [&::-moz-range-thumb]:cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, rgba(255,255,255,0.6) ${(isMuted ? 0 : volume) * 100}%, rgba(255,255,255,0.1) ${(isMuted ? 0 : volume) * 100}%)`,
                  }}
                  data-testid="station-volume"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer Row */}
        <div className="border-t border-white/5 flex items-center divide-x divide-white/5">
          {history.length > 0 && (
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex-1 flex items-center justify-center gap-2 py-3 text-white/50 hover:text-white hover:bg-white/5 transition text-xs font-medium uppercase tracking-wide"
            >
              <Clock className="w-3.5 h-3.5" /> Song History
            </button>
          )}
          <button
            onClick={onShare}
            className="flex-1 flex items-center justify-center gap-2 py-3 text-white/50 hover:text-white hover:bg-white/5 transition text-xs font-medium uppercase tracking-wide"
          >
            {copied ? (
              <><Check className="w-3.5 h-3.5 text-green-400" /><span className="text-green-400">Copied!</span></>
            ) : (
              <><Share2 className="w-3.5 h-3.5" /> Share</>
            )}
          </button>
          <button
            onClick={() => {
              const streamUrl = config.stream_url;
              if (!streamUrl) return;
              const pls = `[playlist]\nNumberOfEntries=1\nFile1=${streamUrl}\nTitle1=${stationName}\nLength1=-1\nVersion=2`;
              const blob = new Blob([pls], { type: "audio/x-scpls" });
              const a = document.createElement("a");
              a.href = URL.createObjectURL(blob);
              a.download = `${stationName.replace(/[^a-zA-Z0-9]/g, "_")}.pls`;
              a.click();
              URL.revokeObjectURL(a.href);
            }}
            className="flex-1 flex items-center justify-center gap-2 py-3 text-white/50 hover:text-white hover:bg-white/5 transition text-xs font-medium uppercase tracking-wide"
          >
            <ListMusic className="w-3.5 h-3.5" /> Playlist
          </button>
        </div>
      </div>

      {/* History Dropdown */}
      {showHistory && history.length > 0 && (
        <div className="mt-2 rounded-xl border border-white/10 bg-black/60 backdrop-blur-lg overflow-hidden">
          {history.slice(0, 8).map((item, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-2.5 border-b border-white/5 last:border-0">
              {item.song?.art ? (
                <img src={item.song.art} alt="" className="w-8 h-8 rounded-lg object-cover" />
              ) : (
                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
                  <Music className="w-3.5 h-3.5 text-white/20" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white text-xs font-medium truncate">{item.song?.title || "Unknown"}</p>
                <p className="text-white/40 text-[10px] truncate">{item.song?.artist || ""}</p>
              </div>
              <span className="text-white/20 text-[10px] flex-shrink-0">
                {item.played_at ? new Date(item.played_at * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* FFT Visualizer */}
      {showVis && (
        <div className="mt-4 w-full max-w-lg mx-auto">
          <FFTVisualizer isPlaying={isPlaying} analyzerRef={analyzerRef} pc={pc} maxWidth={500} />
        </div>
      )}
    </div>
  );
};

export default StationPlayerCard;
